import React, { useState, useEffect } from 'react';
import { Users, Activity, Calendar, TrendingUp, Plus, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AddPatientModal from '../../../components/AddPatientModal';
import SchedulingModal from '../../../components/SchedulingModal';
import ScheduledEmailsList from '../../../components/ScheduledEmailsList';
import { tenantAdminAPI } from '../../../services/api/tenantAdminAPI';
import { useAuth } from '../../../services/contexts/AuthContext';
import toast from 'react-hot-toast';

const TenantAdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalPatients: 0,
    totalStaff: 0,
    recentRecordings: 0,
    pendingAppointments: 0
  });
  const [recentPatients, setRecentPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [showScheduling, setShowScheduling] = useState(false);
  const [scheduledEmailsRefreshKey, setScheduledEmailsRefreshKey] = useState(0);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [patientsResponse, analyticsResponse, activeUsersResponse] = await Promise.all([
        tenantAdminAPI.getPatients(),
        tenantAdminAPI.getAnalyticsSummary(),
        tenantAdminAPI.getUsers({ status: 'active' })
      ]);
      
      setRecentPatients(patientsResponse.data.slice(0, 5));
      
      // Set stats from analytics response
      if (analyticsResponse.data) {
        // Count only active staff members (excluding patients)
        const activeStaffCount = activeUsersResponse.data?.users?.filter(user => 
          user.role === 'tenant_doctor' || 
          user.role === 'tenant_nurse' || 
          user.role === 'tenant_admin' || 
          user.role === 'tenant_technician'
        ).length || 0;

        setStats({
          totalPatients: analyticsResponse.data.patient_count || 0,
          totalStaff: activeStaffCount,
          recentRecordings: analyticsResponse.data.vitals_count || 0,
          pendingAppointments: 0 // Not available yet
        });
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPatient = async (patientData) => {
    try {
      // Create patient using the API
      const response = await tenantAdminAPI.createPatient(patientData);
      console.log('TenantAdminDashboard: Patient created successfully, response:', response);
      toast.success('Patient created successfully!');
      setShowAddPatient(false);
      // Refresh dashboard data to show the new patient
      loadDashboardData();
      
      // Return the created patient data for AddPatientModal to use
      return response.data;
    } catch (error) {
      console.error('Failed to create patient:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Show the actual error message from backend
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create patient';
      toast.error(errorMessage);
      
      // Re-throw the error so AddPatientModal can handle it properly
      throw error;
    }
  };

  const handleScheduleSubmit = async (scheduleData) => {
    try {
      // The API call is already handled in the SchedulingModal
      console.log('Schedule data:', scheduleData);
      
      // Refresh the scheduled emails list to show the new schedule
      setScheduledEmailsRefreshKey(prev => prev + 1);
      
      // Show success message
      toast.success('Schedule created successfully!');
      
      // Close the scheduling modal
      setShowScheduling(false);
    } catch (error) {
      console.error('Failed to create schedule:', error);
      toast.error('Failed to create schedule');
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = "vital-blue", change = null }) => {
    const colorClasses = {
      'vital-blue': 'bg-[#DCEAF5] text-[#207CE5]',
      'cyan-core': 'bg-[#DCEAF5] text-[#00BCD4]',
      'teal-signal': 'bg-[#DCEAF5] text-[#008080]',
      'steel-blue': 'bg-[#DCEAF5] text-[#4682B4]'
    };
    
    const bgClass = colorClasses[color] || colorClasses['vital-blue'];
    
    return (
      <div className="bg-[#F9FBFC] rounded-lg shadow p-6 border border-[#DCEAF5]">
        <div className="flex items-start">
          <div className={`p-3 rounded-lg ${bgClass.split(' ')[0]} mr-4 flex-shrink-0`}>
            <Icon className={`h-6 w-6 ${bgClass.split(' ')[1]}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#3D3D3D]">{title}</p>
            <p className="text-2xl font-bold text-[#0B1C2C]">{value}</p>
            {change && (
              <p className={`text-sm ${change > 0 ? 'text-[#008080]' : 'text-[#00BCD4]'}`}>
                {change > 0 ? '+' : ''}{change}% from last month
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-black font-bold text-lg">Welcome back,</p>
          <p className="text-[#3D3D3D] text-xl">{user?.name}</p>
        </div>

        {/* <div className="flex space-x-3">
          <button
            onClick={() => setShowAddPatient(true)}
            className="bg-[#207CE5] text-white px-4 py-2 rounded-lg hover:bg-[#4682B4] flex items-center"
          >
            <UserPlus className="h-5 w-5 mr-2" />
            Add Patient
          </button>
        </div> */}

      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Patients"
          value={stats.totalPatients}
          icon={Users}
          color="vital-blue"
        />
        <StatCard
          title="Staff Members"
          value={stats.totalStaff}
          icon={UserPlus}
          color="cyan-core"
        />
        <StatCard
          title="Total Recordings"
          value={stats.totalRecordings || stats.recentRecordings}
          icon={Activity}
          color="teal-signal"
        />
        {/* <StatCard
          title="Pending Appointments"
          value={stats.pendingAppointments}
          icon={Calendar}
          color="steel-blue"
        /> */}
      </div>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Patients */}
        <div className="bg-[#F9FBFC] rounded-lg shadow border border-[#DCEAF5]">
          <div className="px-6 py-4 border-b border-[#DCEAF5]">
            <h3 className="text-lg font-medium text-[#0B1C2C]">Recent Patients</h3>
          </div>
          <div className="p-6">
            {recentPatients.length === 0 ? (
              <p className="text-[#3D3D3D] text-center py-8">No patients added yet</p>
            ) : (
              <div className="space-y-4">
                {recentPatients.map((patient) => (
                  <div key={patient.id} className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-[#DCEAF5] rounded-full flex items-center justify-center">
                      <span className="text-[#207CE5] font-semibold text-sm">
                        {patient.alias.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#0B1C2C]">{patient.alias}</p>
                      <p className="text-sm text-[#3D3D3D]">
                        last recording date {new Date(patient.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-sm text-[#3D3D3D]">
                      {/* <span>vital signs</span> */}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-[#F9FBFC] rounded-lg shadow border border-[#DCEAF5]">
          <div className="px-6 py-4 border-b border-[#DCEAF5]">
            <h3 className="text-lg font-medium text-[#0B1C2C]">Quick Actions</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setShowAddPatient(true)}
                className="p-4 border-2 border-dashed border-[#DCEAF5] rounded-lg hover:border-[#207CE5] hover:bg-[#DCEAF5] transition-colors"
              >
                <UserPlus className="h-8 w-8 text-[#3D3D3D] mx-auto mb-2" />
                <p className="text-sm font-medium text-[#3D3D3D]">Add Patient</p>
              </button>
              <button 
                onClick={() => navigate('/tenant-admin/users')}
                className="p-4 border-2 border-dashed border-[#DCEAF5] rounded-lg hover:border-[#00BCD4] hover:bg-[#DCEAF5] transition-colors"
              >
                <Users className="h-8 w-8 text-[#3D3D3D] mx-auto mb-2" />
                <p className="text-sm font-medium text-[#3D3D3D]">Manage Staff</p>
              </button>
              <button 
                onClick={() => setShowScheduling(true)}
                className="p-4 border-2 border-dashed border-[#DCEAF5] rounded-lg hover:border-[#4682B4] hover:bg-[#DCEAF5] transition-colors"
              >
                <Calendar className="h-8 w-8 text-[#3D3D3D] mx-auto mb-2" />
                <p className="text-sm font-medium text-[#3D3D3D]">Schedule</p>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scheduled Emails Section */}
      <div className="mt-6">
        <ScheduledEmailsList key={scheduledEmailsRefreshKey} />
      </div>

      {/* Add Patient Modal */}
      {showAddPatient && (
        <AddPatientModal
          onClose={() => setShowAddPatient(false)}
          onSubmit={handleAddPatient}
          currentTenantId={user?.tenant_id}
          onScheduleCreated={() => {
            // Refresh the scheduled emails list when a schedule is created
            setScheduledEmailsRefreshKey(prev => prev + 1);
          }}
        />
      )}

      {/* Scheduling Modal */}
      {showScheduling && (
        <SchedulingModal
          isOpen={showScheduling}
          onClose={() => setShowScheduling(false)}
          onSubmit={handleScheduleSubmit}
          existingSchedules={[]}
        />
      )}
    </div>
  );
};

export default TenantAdminDashboard;